import { useState } from 'react';
import { RelatedApps, RELATED_BY_APP } from '@hypneuronerd/ui';
import type { AftercarePlan } from '../data/types';
import {
  copyToClipboard,
  downloadFile,
  planToMarkdown,
  planToText,
} from '../lib/exportFormats';
import styles from './RecipeCard.module.css';

interface RecipeCardProps {
  plan: AftercarePlan;
  saved: boolean;
  onSave: () => void;
  onEdit: () => void;
  onAnother: () => void;
}

export function RecipeCard({ plan, saved, onSave, onEdit, onAnother }: RecipeCardProps) {
  const [feedback, setFeedback] = useState<string | null>(null);

  function ping(msg: string) {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 1800);
  }

  async function handleCopy() {
    const ok = await copyToClipboard(planToText(plan));
    ping(ok ? 'copied to clipboard' : 'copy failed — try selecting manually');
  }

  function handleMarkdown() {
    const safe = plan.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    downloadFile(`aftercare-${safe || 'recipe'}.md`, planToMarkdown(plan), 'text/markdown');
    ping('downloaded markdown');
  }

  function handlePrint() {
    window.print();
  }

  return (
    <article className={styles.card}>
      <header className={styles.header}>
        <span className={styles.ornament} aria-hidden="true">
          ❀
        </span>
        <h1 className={styles.title}>{plan.title}</h1>
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>what you will need</h2>
        <ul className={styles.checklist}>
          {plan.items.map((item, i) => (
            <li key={i}>
              <label>
                <input type="checkbox" />
                <span>{item}</span>
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>first 30 minutes</h2>
        <ol className={styles.timeline}>
          {plan.first30.map((step, i) => (
            <li key={i}>
              <label>
                <input type="checkbox" />
                <span>
                  <span className={styles.timeMarker}>{step.time}</span> — {step.action}
                </span>
              </label>
            </li>
          ))}
        </ol>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>next few hours</h2>
        <ol className={styles.timeline}>
          {plan.nextHours.map((step, i) => (
            <li key={i}>
              <label>
                <input type="checkbox" />
                <span>
                  <span className={styles.timeMarker}>{step.time}</span> — {step.action}
                </span>
              </label>
            </li>
          ))}
        </ol>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>for the next day or two</h2>
        <p className={styles.dropBody}>{plan.nextDays}</p>
      </section>

      <footer className={styles.footer}>
        <p className={styles.signature}>{plan.signature}</p>
      </footer>

      <div className={styles.actions}>
        {!saved && (
          <button type="button" className={styles.actionPrimary} onClick={onSave}>
            save this recipe
          </button>
        )}
        {saved && <span className={styles.savedBadge}>✓ saved</span>}
        <button type="button" className={styles.action} onClick={handlePrint}>
          print
        </button>
        <button type="button" className={styles.action} onClick={handleCopy}>
          copy as text
        </button>
        <button type="button" className={styles.action} onClick={handleMarkdown}>
          download .md
        </button>
        <button type="button" className={styles.action} onClick={onEdit}>
          edit inputs
        </button>
        <button type="button" className={styles.action} onClick={onAnother}>
          make another
        </button>
      </div>

      {feedback && (
        <div className={styles.feedback} role="status" aria-live="polite">
          {feedback}
        </div>
      )}

      <RelatedApps apps={RELATED_BY_APP['aftercare-recipe']} surface="cream" />
    </article>
  );
}
